import * as React from "react"
import * as SwitchPrimitive from "@radix-ui/react-switch"
import { cva } from "class-variance-authority"
import { cn } from "@/lib/utils"

const switchVariants = cva(
  "peer cursor-pointer focus-visible:border-ring focus-visible:ring-ring/50 dark:data-[state=unchecked]:bg-input/80 inline-flex shrink-0 items-center rounded-full shadow-xs transition-all duration-200 outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "data-[state=checked]:bg-[#ff895f] data-[state=unchecked]:bg-gray-300",
        "theme": "switch-checked switch-unchecked"
      },
      size: {
        "18": "h-[18px] w-[30.6px]",
        "20": "h-[20px] w-[34px]",
        "32": "h-[32px] w-[54.4px]",
      }
    }
  }
)

const thumbVariants = cva(
  "dark:data-[state=unchecked]:bg-foreground dark:data-[state=checked]:bg-primary-foreground pointer-events-none block rounded-full ring-0 transition-all duration-300 ease-in-out",
  {
    variants: {
      variant: {
        default: "bg-white",
        "theme": "bg-theme-0"
      },
      size: {
        "18": "size-[13.5px] data-[state=unchecked]:translate-x-[2.25px] data-[state=checked]:translate-x-[14.85px]",
        "20": "size-[15px] data-[state=unchecked]:translate-x-[2.5px] data-[state=checked]:translate-x-[16.5px]",
        "32": "size-[24px] data-[state=unchecked]:translate-x-[4px] data-[state=checked]:translate-x-[26.4px]",
      }
    }
  }
)

function Switch({
  className,
  variant = "theme",
  height = 32,
  ...props
}: React.ComponentProps<typeof SwitchPrimitive.Root> & { variant?: "default" | "theme", height?: number }) {
  // Map supported sizes to static classes (no inline styles)
  const sizeVariant = (height === 18 ? "18" : height === 20 ? "20" : "32") as "18" | "20" | "32"

  return (
    <SwitchPrimitive.Root
      data-slot="switch"
      className={cn(switchVariants({ variant, size: sizeVariant }), className)}
      {...props}
    >
      <SwitchPrimitive.Thumb
        data-slot="switch-thumb"
        className={cn(thumbVariants({ variant, size: sizeVariant }))}
      />
    </SwitchPrimitive.Root>
  )
}

export { Switch }
